import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const JobMasterTable = ({
  jobMasterList,
  loader,
  page,
  rowsPerPage,
  total,
  setPage,
  setRowsPerPage,
  handleEdit,
  handleDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const data = jobMasterList || [];

  // Loader
  if (loader) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        Job Master List
      </Typography>

      {/* ✅ Desktop View */}
      {!isMobile ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976d2" }}>
                <TableCell sx={{ color: "white" }}>S.No</TableCell>
                <TableCell sx={{ color: "white" }}>Job Code</TableCell>
                <TableCell sx={{ color: "white" }}>Job Name</TableCell>
                <TableCell sx={{ color: "white" }}>Category</TableCell>
                <TableCell sx={{ color: "white" }}>Duration</TableCell>
                <TableCell sx={{ color: "white" }}>Status</TableCell>
                <TableCell sx={{ color: "white" }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.length > 0 ? (
                data.map((job, index) => (
                  <TableRow key={job.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{job.job_code}</TableCell>
                    <TableCell>{job.job_name}</TableCell>
                    <TableCell>{job.job_category_name}</TableCell>
                    <TableCell>{job.default_duration_min} mins</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          color: job.is_active ? "green" : "red",
                          fontWeight: "bold",
                        }}
                      >
                        {job.is_active ? "ACTIVE" : "INACTIVE"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(job)}
                        sx={{
                          backgroundColor: "primary.main",
                          color: "white",
                          "&:hover": { backgroundColor: "primary.dark" },
                          mr: 0.5,
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(job.id)}
                        sx={{
                          backgroundColor: "error.main",
                          color: "white",
                          "&:hover": { backgroundColor: "error.dark" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* ✅ Mobile View */
        <Box>
          {data.length > 0 ? (
            data.map((job, index) => (
              <Card
                key={job.id}
                sx={{
                  mb: 2,
                  borderLeft: "5px solid #1976d2",
                  boxShadow: 2,
                }}
              >
                <CardContent>
                  {/* Top Row */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: "bold", color: "#1976d2" }}
                    >
                      #{page * rowsPerPage + index + 1}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: job.is_active ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {job.is_active ? "ACTIVE" : "INACTIVE"}
                    </Typography>
                  </Box>

                  {/* Main Info */}
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {job.job_name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Code: {job.job_code}
                  </Typography>

                  <Typography variant="body2">
                    Category: {job.job_category_name}
                  </Typography>

                  <Typography variant="body2">
                    Duration: {job.default_duration_min} mins
                  </Typography>
                </CardContent>
                <Box sx={{ display: "flex", gap: 1, p: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(job)}
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(job.id)}
                    sx={{
                      backgroundColor: "error.main",
                      color: "white",
                      "&:hover": { backgroundColor: "error.dark" },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            ))
          ) : (
            <Typography align="center">No Data Found</Typography>
          )}
        </Box>
      )}

      {/* ✅ Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{ mt: 2 }}
      />
    </Box>
  );
};

export default JobMasterTable;
